import { useCallback, useContext, useMemo } from 'react';
import DateInputContext from '../../contexts/DateInputContext';
import buildDateRange from './buildDateRange';
import AccountContext from '../../contexts/AccountContext';
import useSelectedBonusPeriod from '../useSelectedBonusPeriod';
import FilterPlatesContext from '../../contexts/FilterPlatesContext';
import PeriodsContext from '../../contexts/PeriodsContext';
import GridContext from '../../contexts/GridContext';
import CardContext from '../../contexts/CardContext';
import WeekStartsOnOverrideContext from '../../contexts/WeekStartsOnOverrideContext';
import isV5ChartDef from '../../types/visTypeCheckers/isV5ChartDef';
import isSingleMetricDateMatrix from '../../types/visTypeCheckers/isSingleMetricDateMatrix';
import PopupContext from 'contexts/PopupContext';
import filterPlateTypeCheckers from '../../types/filterPlateTypeCheckers';
import filterTypeCheckers from '../../components/FilterPlateForm/filterTypeCheckers';
import FilterPlateFormContext from '../../contexts/FilterPlateFormContext';
import { AccountTodayOverrideContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import { FiscalPeriodsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';

const applyDateField = (
  dateRangeInput: DateRangeInput,
  dateField?: string,
): DateRangeInput => {
  return {
    ...dateRangeInput,
    dateField,
  };
};

const DEFAULT_DATE_SCOPE: DateRangeInput = {};

export interface BuildDateScopeArgs {
  comparison?: PersistedComparisonType;
  // Repeat the comparison this many times, e.g. 2 years ago
  comparisonSteps?: number;
  // In place of the date field from DateInputContext
  dateField?: string;
  // The interval a chart trending by 'auto' resolved to, so its comparison
  // is taken as the interval's (e.g. whole weeks back for a weekly trend)
  trendInterval?: AutoInterval['interval'];
}

/**
 * Builds the date scope the way useDateScope does, for any number of
 * comparisons in one render (e.g. a series per earlier period).
 */
export const useBuildDateScope = ({
  isMetricListItem,
}: {
  isMetricListItem?: boolean;
}) => {
  const { chartDefinition: chartDef } = useContext(CardContext);
  const { dateScopeOverride } = useContext(GridContext);
  const { bonusPeriods, selectedBonusPeriod } = useSelectedBonusPeriod();
  const { weekStartsOn: accountWeekStartsOn, timezone } =
    useContext(AccountContext);
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { weekStartsOnOverride } = useContext(WeekStartsOnOverrideContext);
  const weekStartsOn = weekStartsOnOverride
    ? weekStartsOnOverride
    : accountWeekStartsOn;
  const { relativeDateRange, dateRange, advancedRelativeDateRange, dateField } =
    useContext(DateInputContext);
  const { drillDowns } = useContext(FilterPlatesContext);
  const { selectedPeriod, periods } = useContext(PeriodsContext);
  const { poppedUpKpi, selectedReport, selectedBoard } =
    useContext(PopupContext);
  const { isDateScopeDisabled } = useContext(FilterPlateFormContext);
  const { allFiscals } = useContext(FiscalPeriodsContext);

  const buildDateScope = useCallback(
    ({
      comparison,
      comparisonSteps,
      dateField: dateFieldOverride,
      trendInterval,
    }: BuildDateScopeArgs = {}): DateRangeInput => {
      if (isDateScopeDisabled) {
        return DEFAULT_DATE_SCOPE;
      }
      if (dateScopeOverride) {
        return dateScopeOverride;
      }
      const dateDrill = drillDowns.find(
        (d) =>
          filterPlateTypeCheckers.isFixed(d) &&
          filterTypeCheckers.isDateFilter(d.fixedValue) &&
          !!d.fixedValue.dateRangeValues,
      );

      const overrideDateRange = (() => {
        if (
          !dateDrill ||
          !chartDef ||
          !filterPlateTypeCheckers.isFixed(dateDrill) ||
          (filterTypeCheckers.isDateFilter(dateDrill.fixedValue) &&
            !dateDrill.fixedValue.dateRangeValues)
        ) {
          return undefined;
        }

        const isV5WithDateDimension =
          isV5ChartDef(chartDef) &&
          chartDef.dimensionA &&
          chartDef.dimensionA.fieldType === 'date';

        if (isV5WithDateDimension || isSingleMetricDateMatrix(chartDef)) {
          const chartTrendingInterval =
            isV5ChartDef(chartDef) &&
            chartDef.trendByCalendarInterval &&
            chartDef.trendByCalendarInterval;
          const plateTrendingInterval =
            dateDrill && dateDrill.fixedValue && dateDrill.fixedValue.field;
          const isLastDrillDate =
            drillDowns[drillDowns.length - 1].id === dateDrill.id;
          if (
            isLastDrillDate &&
            chartTrendingInterval === plateTrendingInterval
          ) {
            return undefined;
          }
        }

        if (
          !filterTypeCheckers.isDateFilter(dateDrill.fixedValue) ||
          !dateDrill.fixedValue.dateRangeValues
        ) {
          return undefined;
        }
        return dateDrill.fixedValue.dateRangeValues[0].value;
      })();

      return applyDateField(
        buildDateRange({
          relativeDateRange: overrideDateRange ? undefined : relativeDateRange,
          dateRange: overrideDateRange ? overrideDateRange : dateRange,
          advancedRelativeDateRange: overrideDateRange
            ? undefined
            : advancedRelativeDateRange,
          comparison,
          comparisonSteps,
          weekStartsOn,
          chartDef:
            trendInterval &&
            chartDef &&
            isV5ChartDef(chartDef) &&
            chartDef.trendByCalendarInterval === 'auto'
              ? { ...chartDef, trendByCalendarInterval: trendInterval }
              : chartDef,
          isMetricListItem,
          selectedBonusPeriod,
          bonusPeriods,
          selectedPeriod,
          periods,
          timezone,
          isScorecardPopupReport: !!poppedUpKpi && !!selectedReport,
          isUpcomingEventsBoard:
            !!selectedBoard && selectedBoard.dataType === 'upcomingEvents',
          todayOverride,
          allFiscals,
        }),
        dateFieldOverride || dateField,
      );
    },
    [
      isDateScopeDisabled,
      dateScopeOverride,
      drillDowns,
      relativeDateRange,
      dateRange,
      advancedRelativeDateRange,
      weekStartsOn,
      chartDef,
      isMetricListItem,
      selectedBonusPeriod,
      bonusPeriods,
      selectedPeriod,
      periods,
      timezone,
      poppedUpKpi,
      selectedReport,
      selectedBoard,
      todayOverride,
      dateField,
      allFiscals,
    ],
  );

  return buildDateScope;
};

const useDateScope = ({
  comparison,
  isMetricListItem,
  trendInterval,
}: {
  comparison?: PersistedComparisonType;
  isMetricListItem?: boolean;
  // See BuildDateScopeArgs
  trendInterval?: AutoInterval['interval'];
}) => {
  const buildDateScope = useBuildDateScope({ isMetricListItem });

  const dateScope: DateRangeInput = useMemo(
    () => buildDateScope({ comparison, trendInterval }),
    [buildDateScope, comparison, trendInterval],
  );

  return dateScope;
};

export default useDateScope;
